import {
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import Lightbox, { SlideImage } from "yet-another-react-lightbox";
import Inline from "yet-another-react-lightbox/plugins/inline";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
import { GalleryImage } from "../../types/images";
import styles from "./Gallery.module.css";

export type GalleryHandle = {
  fullScreen: () => void;
};

type GalleryProps = {
  images: GalleryImage[];
};

function toSlides(images: GalleryImage[]): SlideImage[] {
  return images.map((img) => ({
    src: img.original,
    alt: img.originalAlt,
    thumbnail: img.thumbnail,
  }));
}

const Gallery = forwardRef<GalleryHandle, GalleryProps>(function Gallery(
  { images },
  ref
) {
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const slides = toSlides(images);

  useImperativeHandle(ref, () => ({
    fullScreen: () => setOpen(true),
  }));

  if (slides.length === 0) {
    return null;
  }

  const multi = slides.length > 1;
  const hideNav = !multi
    ? { buttonPrev: () => null, buttonNext: () => null }
    : undefined;

  return (
    <div className={styles.gallery}>
      <Lightbox
        index={index}
        slides={slides}
        plugins={multi ? [Inline, Thumbnails] : [Inline]}
        carousel={{ padding: 0, spacing: 0, imageFit: "contain" }}
        inline={{ className: styles.inline }}
        thumbnails={{ position: "bottom", border: 0, padding: 8, gap: 8 }}
        render={hideNav}
        on={{
          view: ({ index: next }) => setIndex(next),
          click: () => setOpen(true),
        }}
      />
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={index}
        slides={slides}
        plugins={multi ? [Zoom, Thumbnails, Counter] : [Zoom]}
        carousel={{ imageFit: "contain" }}
        thumbnails={{ position: "bottom", border: 0, padding: 8, gap: 8 }}
        render={hideNav}
        zoom={{
          maxZoomPixelRatio: 4,
          scrollToZoom: true,
        }}
        on={{
          view: ({ index: next }) => setIndex(next),
        }}
      />
    </div>
  );
});

export default Gallery;
